import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { LoadingOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';

import JobListCard from '../../../../components/Card/JobListCard';
import useQueryString from '../../../../hooks/ui-control/useQueryString';
import { useAppSelector } from '../../../../redux/store';

interface InvitedJobListProps {
  showMobileInfo?: boolean;
  setShowMobileInfo: (visible: boolean) => void;
  loading: boolean;
}

const InvitedJobList: React.FC<InvitedJobListProps> = ({
  showMobileInfo,
  setShowMobileInfo,
  loading,
}) => {
  // URL query params hook
  const [queryParams, setQueryParams] = useQueryString();
  const currentJobId = queryParams.get('jobId');

  // Invited jobs from Redux slice
  const invitedJobs = useAppSelector((state) => state.invitedJobs.invitedJobs);

  // Track if initial URL had jobId
  const hadInitialJobId = useRef<boolean>(!!currentJobId);

  // Parse invite state from sessionStorage
  const raw = sessionStorage.getItem('invt');
  const [inviteData, setInviteData] = useState<{
    state: boolean;
    jobId: string;
  }>(raw ? JSON.parse(raw) : { state: false, jobId: '' });

  // On mount: set mobile info visibility based on initial jobId
  useEffect(() => {
    setShowMobileInfo(hadInitialJobId.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearInvite = useCallback(() => {
    sessionStorage.removeItem('invt');
    setInviteData({ state: false, jobId: '' });
  }, []);

  useEffect(
    () => {
      let timer: number | undefined;

      // 1️⃣ Invite-mode branch: set URL to the invited ID, then clear the invite flag in 3 s
      if (inviteData.state) {
        setQueryParams({ jobId: inviteData.jobId });

        timer = window.setTimeout(() => {
          clearInvite();
        }, 10000);

        // cleanup any pending timer if effect re-runs
        return () => {
          if (timer) clearTimeout(timer);
        };
      }

      // 2️⃣ Normal branch: auto-pick the first job whenever your list changes
      if (invitedJobs.length > 0) {
        const currentJobId = queryParams.get('jobId');

        if (
          !currentJobId ||
          !invitedJobs.some((inv) => inv?.job?.id === currentJobId)
        ) {
          setQueryParams({ jobId: invitedJobs[0].job.id });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      invitedJobs,
      inviteData.state,
      inviteData.jobId,
      // queryParams,
      // setQueryParams,
      // clearInvite,
    ]
  );

  // Handle card click: update URL and toggle mobile info
  const handleJobClick = (jobId: string) => {
    setQueryParams({ jobId });
    setShowMobileInfo(!showMobileInfo);
    if (inviteData) {
      clearInvite();
    }
  };

  return (
    <Container className={showMobileInfo ? 'showMobileInfo' : ''}>
      {loading ? (
        <LoadingOutlined />
      ) : invitedJobs.length === 0 ? (
        <p>No invited jobs available</p>
      ) : (
        invitedJobs.map((inv) => (
          <JobListCard
            key={inv.job?.id}
            imgSrc="/static/img/company-placeholder.png"
            institutionName={inv.job?.institutionName}
            jobTitle={inv.job?.title}
            yearsOfExperience={inv.job?.yearsOfExperience}
            pay={inv.job?.pay}
            onClick={() => handleJobClick(inv.job?.id)}
            className={clsx(
              'cursor-pointer inv',
              inv.job?.id === currentJobId && 'active'
            )}
            jobId={inv.job?.id}
            jobDescription={inv.job?.jobDescription}
          />
        ))
      )}
    </Container>
  );
};

export default InvitedJobList;

// ------ Styled Components ------

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-radius: 12px;

  @media (max-width: 884px) {
    display: grid;
    &.showMobileInfo {
      display: none;
    }
  }
`;
